'use client';

import { useState } from 'react';
import { ContactSection } from '@/types/sanity';
import { SectionCTAs } from './SectionCTAs';
import { PortableText } from '@portabletext/react';
import { HubSpotFormModal } from '@/components/hubspot/HubSpotFormModal';
import { Button } from '@/components/ui/button';
import { Mail, Phone, MapPin } from 'lucide-react';
import { ScrollReveal } from '@/components/animations/ScrollAnimations';

import { cn } from '@/lib/utils';

interface ContactSectionComponentProps {
  section: ContactSection;
  pageSlug?: string;
}

const iconMap = {
  email: Mail,
  phone: Phone,
  address: MapPin,
} as const;

export function ContactSectionComponent({ section, pageSlug }: ContactSectionComponentProps) {
  const [formOpen, setFormOpen] = useState(false);
  const hasContactDetails = section.contactDetails && section.contactDetails.length > 0;

  return (
    <ScrollReveal direction="up" delay={0.1}>
      <section
        className={cn(
          'contact-section py-8 md:py-12 lg:py-16',
          pageSlug && `contact-section--${pageSlug}`
        )}
      >
        <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
          <div className="px-6 sm:px-0">
            <div className="mb-8 md:mb-10 text-center">
              {section.title && (
                <h2 className="font-medium mb-3 text-section-title leading-section-title">
                  {section.title}
                </h2>
              )}
              {section.subtitle && (
                <p className="text-muted-foreground text-section-subtitle leading-section-subtitle">
                  {section.subtitle}
                </p>
              )}
              {section.description && (
                <div className="prose max-w-2xl mx-auto mt-4 text-muted-foreground">
                  <PortableText value={section.description} />
                </div>
              )}
            </div>

            <div
              className={cn(
                'max-w-4xl mx-auto',
                hasContactDetails && 'grid grid-cols-1 md:grid-cols-2 gap-12'
              )}
            >
              {/* Contact details */}
              {hasContactDetails && (
                <div className="space-y-6">
                  {section.contactDetails!.map((detail) => {
                    const Icon = iconMap[detail.type];
                    return (
                      <div key={detail._key} className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{detail.label}</p>
                          {detail.type === 'email' ? (
                            <a
                              href={`mailto:${detail.value}`}
                              className="text-muted-foreground hover:text-primary transition-colors"
                            >
                              {detail.value}
                            </a>
                          ) : detail.type === 'phone' ? (
                            <a
                              href={`tel:${detail.value.replace(/\s/g, '')}`}
                              className="text-muted-foreground hover:text-primary transition-colors"
                            >
                              {detail.value}
                            </a>
                          ) : (
                            <p className="text-muted-foreground">{detail.value}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* CTA to open form */}
              {section.hubspotFormId && (
                <div
                  className={cn(
                    'flex flex-col items-center justify-center',
                    !hasContactDetails && 'text-center'
                  )}
                >
                  <Button size="lg" onClick={() => setFormOpen(true)}>
                    Get in touch
                  </Button>
                  <HubSpotFormModal
                    formId={section.hubspotFormId}
                    open={formOpen}
                    onOpenChange={setFormOpen}
                    title={section.title}
                    description={section.subtitle}
                  />
                </div>
              )}
            </div>

            <SectionCTAs ctas={section.ctas} />
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
